"use client";

import { useState, useEffect, Fragment } from "react";
import Link from "next/link";
import Image from "next/image";
import { Avatar } from "@/components/ui/Avatar";
import { usePermissions } from "@/contexts/PermissionContext";
import { cn } from "@/lib/utils";
import { GLOBAL_ZONES, filterByPermission, isActive } from "./nav-model";
import { UserMenu } from "./UserMenu";
import type { Locale } from "@/i18n-config";
import type { Dictionary } from "@/types/dictionary";
import type { User } from "@/types/auth";

interface RailProps {
  user?: User;
  lang: Locale;
  dict: Dictionary;
  pathname: string;
}

export function Rail({ user, lang, dict, pathname }: RailProps) {
  const { hasPermission, isSystemAdmin } = usePermissions();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  const zones = filterByPermission(GLOBAL_ZONES, {
    hasPermission,
    isSystemAdmin,
    role: user?.role,
  });
  const s = dict?.layout?.sidebar as Record<string, string> | undefined;

  return (
    <aside className="flex flex-col w-[var(--rail-width)] h-screen bg-secondary border-r border-border shrink-0">
      <Link
        href={`/${lang}`}
        className="flex items-center justify-center h-[60px] border-b border-border shrink-0"
      >
        <Image
          src="/venuer-icon.png"
          alt="Venuer"
          width={28}
          height={28}
          className="w-7 h-7"
          priority
        />
      </Link>

      <nav className="flex-1 min-h-0 py-2.5 flex flex-col items-center gap-[3px] overflow-y-auto">
        {zones.map((zone) => {
          const href = `/${lang}${zone.href}`;
          const active = isActive(pathname, href);
          const Icon = zone.icon;
          const label = (zone.dictKey && s?.[zone.dictKey]) || zone.fallback;
          return (
            <Fragment key={zone.key}>
              {zone.key === "admin" && (
                <div className="h-px w-[46px] bg-border my-2" />
              )}
              <Link
                href={href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex w-[76px] flex-col items-center gap-[5px] py-[9px] rounded-[12px] text-center transition-colors focus-ring",
                  "text-[10px] font-medium leading-none",
                  active
                    ? "bg-brand-glow-12 text-brand-light"
                    : "text-muted hover:bg-brand-glow-08 hover:text-brand-light",
                )}
              >
                <Icon className="w-[22px] h-[22px] shrink-0" aria-hidden />
                <span>{label}</span>
              </Link>
            </Fragment>
          );
        })}
      </nav>

      <div className="border-t border-border py-3 flex justify-center shrink-0 relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={
            (dict?.layout?.header as Record<string, string> | undefined)
              ?.userMenu ?? "Kullanıcı menüsü"
          }
          className="p-1 rounded-full hover:bg-brand-glow-08 transition-colors focus-ring"
        >
          <Avatar name={user?.name || "Kullanıcı"} size="sm" />
        </button>

        {menuOpen && (
          <>
            <div
              className="fixed inset-0 z-30"
              onClick={() => setMenuOpen(false)}
              aria-hidden
            />
            <div className="absolute left-full bottom-0 ml-2 z-40">
              <UserMenu
                user={user}
                lang={lang}
                dict={dict}
                onClose={() => setMenuOpen(false)}
              />
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
